"use client";

import { FC, useState } from "react";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { useWorkspaceStore } from "@/store/workspace/workspace-store";
import { Workspace } from "@/services/workspace-service";
import { domId } from "@/lib/dom-id";
import { coerceLocale } from "@/i18n/locales";
import { withLocalePath } from "@/lib/locale-routing";
import { pushWithMinimumDelay } from "@/lib/navigation";
import { dropConnectorViewParams } from "@/lib/connector-view-params";
import { Check, ChevronDown } from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Some environments provide `workspace.name` already suffixed with the workspace id
 * (e.g. "Acme (ws_123)" or "Acme - ws_123").
 * We only want to display the friendly name in the UI.
 */
const getWorkspaceNameOnly = (workspace?: Workspace | null) => {
  if (!workspace?.name) return "";

  const raw = workspace.name;
  const id = workspace.workspace_id;
  if (!id) return raw;

  const idEscaped = escapeRegExp(id);
  const patterns: RegExp[] = [
    new RegExp(`\\s*\\(\\s*${idEscaped}\\s*\\)\\s*$`), // "Name (id)"
    new RegExp(`\\s*[-–—:]\\s*${idEscaped}\\s*$`), // "Name - id" / "Name: id"
    new RegExp(`\\s+${idEscaped}\\s*$`), // "Name id"
  ];

  let cleaned = raw;
  for (const pattern of patterns) cleaned = cleaned.replace(pattern, "");

  cleaned = cleaned.trim();
  return cleaned || raw;
};

export const WorkspaceSelector: FC = () => {
  const t = useTranslations("WorkspaceSelector");
  const router = useRouter();
  const locale = useLocale();
  const activeLocale = coerceLocale(locale);
  const [isOpen, setIsOpen] = useState(false);

  const { workspaces, selectedWorkspace } = useWorkspaceStore();

  const handleSelectWorkspace = (workspace: Workspace) => {
    // Don't switch if already selected
    if (workspace.workspace_id === selectedWorkspace?.workspace_id) {
      setIsOpen(false);
      return;
    }

    setIsOpen(false);

    // Navigate to home page to fetch data for new workspace
    // while preserving the workspace in the URL.
    const url = new URL(window.location.href);
    url.pathname = withLocalePath(activeLocale, "/");
    url.searchParams.delete("workspace");
    url.searchParams.set("workspace_id", workspace.workspace_id || "");
    // The new workspace's dashboard starts from its default view.
    dropConnectorViewParams(url.searchParams);
    const nextHref = `${url.pathname}${url.search}`;
    void pushWithMinimumDelay(router, nextHref);
  };

  // Don't render if no workspaces available
  if (workspaces.length === 0) {
    return null;
  }

  const selectedWorkspaceName = getWorkspaceNameOnly(selectedWorkspace);
  const triggerBaseClass =
    "flex h-10 items-center gap-2 rounded-full border border-[#B2BBC4] bg-white px-4 text-sm font-medium text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-200";

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid={domId("btn", "workspace-selector-toggle")}
          data-selector-trigger="true"
          variant="unstyled"
          className={triggerBaseClass}
        >
          <span className="truncate">
            {selectedWorkspaceName || t("placeholder")}
          </span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-slate-700 transition-transform duration-150 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="min-w-[240px] max-w-[320px] overflow-hidden rounded-lg border-slate-200 bg-white p-1 shadow-lg"
      >
        {workspaces.map((workspace) => {
          const workspaceName = getWorkspaceNameOnly(workspace) || workspace.name;
          const isSelected =
            selectedWorkspace?.workspace_id === workspace.workspace_id;

          return (
            <DropdownMenuItem
              key={workspace.workspace_id}
              data-testid={domId("btn", `workspace-selector-option-${workspace.workspace_id}`)}
              onSelect={() => handleSelectWorkspace(workspace)}
              className={`w-full px-4 py-2.5 text-left text-sm text-slate-700 ${
                isSelected
                  ? "bg-blue-50 text-blue-700 focus:bg-blue-50 focus:text-blue-700"
                  : ""
              }`}
            >
              <div className="flex w-full items-center justify-between">
                <div className="font-medium leading-5">{workspaceName}</div>
                {isSelected && <Check className="h-5 w-5 text-blue-600" />}
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
